import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import _ from 'lodash';
import { BehaviorSubject, forkJoin, Observable, Subscription, tap } from 'rxjs';
import { getResourceCategoryIconPath } from 'src/app/common/categoryUtils';
import { Stack } from 'src/app/common/dataStructures/stack';
import { formatFromDate } from 'src/app/common/datetime_utility';
import { getPlatformLogoPath } from 'src/app/common/platformUtils';
import { owaspLcncToDisplayName } from 'src/app/common/violationRulesUtils';
import { RowActionClicked } from 'src/app/components/table-row/table-row.component';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import {
  ResetViolationsStatusApiModel,
  UpdatableViolationStatus,
  UpdateViolationsStatusApiModel,
  ViolationChangeJustificationApiModel,
  ViolationModel,
  ViolationsActionBulkApiModel,
  ViolationsDataType,
  ViolationsStatusUpdateResponse,
  ViolationStatus,
} from 'src/app/models/api/violations.model';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFalgsTypes.enum';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { HygieneFiltersModel } from 'src/app/models/filters/implementations/pageFilters/hygieneFilters.model';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { MenuAction } from 'src/app/models/menuAction.model';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { HygieneTableColumnsModel } from 'src/app/models/tableColumns/hygieneTableColumn.model';
import { IViolationsViewModel } from 'src/app/models/viewModels/violationsViewModel';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { getViolationStatusBulkActionType } from 'src/app/services/actions/violations/helpers';
import { ViolationsActionsService } from 'src/app/services/actions/violations/violations-actions.service';
import { ViolationsSupportedActions } from 'src/app/services/actions/violations/violationsSupportedActions';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { ViolationsHttpService } from 'src/app/services/httpServices/violationsHttpService/violations-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { FieldName, TextProviderService } from 'src/app/services/infra/textProviderService/text-provider.service';
import { InventoryHelper } from '../inventory/helpers/inventoryHelper';
import { ViolationRowHelpers } from '../violations/helpers/violationRowHelpers';
import { ChangeEventJustificationDialogComponent } from '../../components/note-dialog/change-event-justification-dialog.component';
import { IChangeEventJustificationComponentOutput } from '../../models/popups/outputs/changeEventJustificationComponentOutput.interface';
import { populateErrorMessage } from '../utils/utils';
import { DisplayNameService } from 'src/app/services/common/displayName/displayName.service';
import { ColumnsOrderService } from 'src/app/services/common/columnsOrder/columns-order.service';
import { TableColumnsNames } from 'src/app/models/tableColumns/tableColumnsNames.enum';
import { SidePanelService } from '../../features/side-panel/services/side-panel.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { AccountCategory } from 'src/app/models/api/accountManagement.model';
import { AccountService } from 'src/app/services/infra/account/account.service';
import { AccessService } from 'src/app/services/infra/access/access.service';

@Component({
  selector: 'app-hygiene',
  templateUrl: './hygiene.component.html',
  styleUrls: ['./hygiene.component.scss'],
})
export class HygieneComponent implements OnInit, OnDestroy {
  public columns = signal<ITableColumn[]>([]);
  public pageName = TableColumnsNames.Hygiene;
  public hygieneCurrentPage$: BehaviorSubject<IViolationsViewModel[]>;
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public showNoData = false;
  public isFiltersMenuOpen = false;

  public readonly defaultPageSize: number;
  public totalItems = 0;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;

  public filterEnums = FilterTypesEnum;
  public optionsAreSelected = false;
  public pageFilters: HygieneFiltersModel;
  public sorts?: SortColumn[];
  public violationsDataType = ViolationsDataType.hygiene;

  public checkedViolationIds: Set<string> = new Set();
  public multiSelectedMenuActions$: BehaviorSubject<MenuAction[]> = new BehaviorSubject<MenuAction[]>([]);
  public multiActionsIsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public allRowsSelected = new FormControl(false);
  public permissionsEnum = PermissionEnum;
  public featureFlagTypes = FeatureFlagTypes;
  public expandFilterResultControl = new FormControl(false);

  public accountCategory$: Observable<AccountCategory | null>;
  public AccountCategory = AccountCategory;

  public accessService = inject(AccessService);

  private _currentViolationsById: { [violationId: string]: ViolationModel } = {};
  private _paginationNavigation: IPaginationNavigationModel;

  private readonly _queryParamsSubscription$: Subscription;
  private _filtersOnChangeSubscription$: Subscription;
  private _sortOnChangeSubscription$: Subscription;
  private readonly _activatedRouteSubscription$: Subscription;

  private _firstPageSubscription$: Subscription;
  private _fetchByPageSubscription$: Subscription;
  private readonly _notAvailableString: string;

  private readonly _storeService = inject(StoreService);
  private readonly _accountService = inject(AccountService);

  constructor(
    public matDialog: MatDialog,
    public logger: LoggerService,
    private readonly _sidePanel: SidePanelService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private readonly _violationsHttpService: ViolationsHttpService,
    private readonly _filtersService: FiltersService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _violationsActionsService: ViolationsActionsService,
    private readonly _permissionsService: PermissionsService,
    private readonly _notificationService: NotificationsService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _textProviderService: TextProviderService,
    private readonly _displayNameService: DisplayNameService,
    private readonly _columnsOrderService: ColumnsOrderService
  ) {
    this.defaultPageSize = this._violationsHttpService.defaultPageSize;
    this._notAvailableString = this._labelsProviderPipe.transform('common.notAvailable');
    this._initializePageInfo();
  }

  ngOnInit(): void {
    this.accountCategory$ = this._accountService.getAccountCategory$();

    this.hygieneCurrentPage$ = this._storeService.getHygienePageDataAsSubject$();

    this.columns.set(
      this._columnsOrderService.getColumnsOrderFromLocalStorage(this.pageName) || HygieneTableColumnsModel
    );

    this.pageFilters = this._filtersService.getFilters(PageWithFiltersEnum.Hygiene) as HygieneFiltersModel;
    this.pageFilters.restoreSortColumnToDefaultIfNeeded();

    this._subscribeToHandleFiltersOnChange();
    this._subscribeToSortOnChange();

    this.allRowsSelected.valueChanges.pipe(tap((value) => this._onAllRowsSelectionClick(value))).subscribe();

    this.expandFilterResultControl.valueChanges.subscribe(() => this._updateSupportedActionsForSelectedRows());
  }

  ngOnDestroy(): void {
    this._filtersOnChangeSubscription$?.unsubscribe();
    this._sortOnChangeSubscription$?.unsubscribe();
    this._queryParamsSubscription$?.unsubscribe();
    this._activatedRouteSubscription$?.unsubscribe();
  }

  public rowSelected(selection: IViolationsViewModel): void {
    this._sidePanel.openViolationDetails(selection.violation.id, selection.integrationId, selection);
  }

  public rowUnselected = () => this._sidePanel.close();

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public filtersOnMenuStateChange(menuState: boolean) {
    this.isFiltersMenuOpen = menuState;
  }

  public actionOnClick(actionClicked: RowActionClicked): void {
    const violationRow = actionClicked.row.getValue() as IViolationsViewModel;

    const response = this._violationsActionsService.handleActionClicked(
      actionClicked.action.name as ViolationsSupportedActions,
      this._currentViolationsById[violationRow.id]
    ) as Observable<ViolationsStatusUpdateResponse | undefined | boolean>;

    switch (actionClicked.action.name) {
      case ViolationsSupportedActions.Exempt:
      case ViolationsSupportedActions.Waive:
        this._handleViolationStatusUpdateActionResponse(
          response as Observable<ViolationsStatusUpdateResponse | undefined>,
          actionClicked
        );
        break;
      case ViolationsSupportedActions.Delete:
        this._handleResourceDeleteActionResponse(response as Observable<boolean>);
        break;
      default:
        break;
    }
  }

  public onPagination(rule: PaginationOperationEnum) {
    if (!this.isLoading$.value) {
      if (rule == PaginationOperationEnum.next) {
        this._paginationNavigation.previousPage.push(this._paginationNavigation.currentPage);
      }

      const pageUrl =
        rule == PaginationOperationEnum.next
          ? this._paginationNavigation.nextPage
          : this._paginationNavigation.previousPage.pop();
      if (pageUrl) {
        this._fetchPageWhenTokenExist(pageUrl);
      }
    }
  }

  public onColumnSortClick(sortData: SortColumn) {
    this.pageFilters.sortedColumn$.next(sortData);
  }

  public multiSelectedActionClick(menuAction: MenuAction) {
    const dialogRef = this.matDialog.open(ChangeEventJustificationDialogComponent, {
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });

    dialogRef.afterClosed().subscribe((justificationOutput?: IChangeEventJustificationComponentOutput) => {
      if (justificationOutput && justificationOutput.addJustification && justificationOutput.content) {
        const violationIdsByIntegration = _.groupBy(
          Object.values(this._currentViolationsById).filter((violation) => this.checkedViolationIds.has(violation.id)),
          (violation: ViolationModel) => violation.integration.id
        );
        const status = menuAction.name as ViolationsSupportedActions;

        if (!this.expandFilterResultControl.value) {
          if (status == ViolationsSupportedActions.Exempt) {
            this._updateCheckedViolationsStatus(
              UpdatableViolationStatus.exempted,
              violationIdsByIntegration,
              justificationOutput.content
            );
          } else if (status == ViolationsSupportedActions.Waive) {
            this._resetCheckedViolationsStatus(violationIdsByIntegration, justificationOutput.content);
          } else {
            throw new Error(`HygieneComponent multiSelectedActionClick: The status ${status} is not supported`);
          }
        } else {
          const statusAction = getViolationStatusBulkActionType(status);
          this.multiActionsIsLoading$.next(true);
          const bulkModel = {
            dataType: ViolationsDataType.hygiene,
            change_reason: {
              justification: justificationOutput.content,
            },
          } as ViolationsActionBulkApiModel;

          this._violationsHttpService
            .applyBulkAction(bulkModel, statusAction, this.pageFilters.getSelectedFilters(), this.sorts)
            .subscribe({
              next: () => {
                this.multiActionsIsLoading$.next(false);
                this._notificationService.success(
                  `Bulk action was submitted, an email notification will be sent upon completion.`
                );
              },
              error: (error) => {
                this.multiActionsIsLoading$.next(false);
                this._httpErrorHandlerService.handle(error, true);
              },
            });
        }
      }
    });
  }

  public onCheckboxClick(event: Set<string>): void {
    this.checkedViolationIds = event;
    this._updateSupportedActionsForSelectedRows();
  }

  public columnsSelected(selectedColumns: ITableColumn[]) {
    this.columns.set(structuredClone(selectedColumns));
  }

  private _updateSupportedActionsForSelectedRows() {
    const menuActions: MenuAction[] = [];
    const userHasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);
    let atLeastOneViolationOpen = false;
    let atLeastOneViolationCustomStatus = false;
    this.checkedViolationIds.forEach((violationId) => {
      const violation = this._currentViolationsById[violationId];
      if (violation.status === ViolationStatus.open) {
        atLeastOneViolationOpen = true;
      }
      if (violation.status !== ViolationStatus.open && violation.status !== ViolationStatus.resolved) {
        atLeastOneViolationCustomStatus = true;
      }
    });

    const notSupportedTooltip = 'Action not applicable';
    const missingPermissionsTooltip = this._labelsProviderPipe.transform('commonActions.operatorActionTooltip');
    let exemptTooltip = notSupportedTooltip;
    let waiveTooltip = notSupportedTooltip;

    if (!userHasPermissions) {
      exemptTooltip = waiveTooltip = missingPermissionsTooltip;
    }

    const tooltip = this.expandFilterResultControl.value ? 'Resolved hygiene rules will be skipped' : '';

    menuActions.push({
      name: ViolationsSupportedActions.Exempt.toString(),
      displayNameLabel: 'violationActions.exempt',
      disabled: !(userHasPermissions && (this.expandFilterResultControl.value || atLeastOneViolationOpen)),
      tooltip: !(userHasPermissions && (this.expandFilterResultControl.value || atLeastOneViolationOpen))
        ? exemptTooltip
        : tooltip,
    } as MenuAction);

    menuActions.push({
      name: ViolationsSupportedActions.Waive.toString(),
      displayNameLabel: 'violationActions.waive',
      disabled: !(userHasPermissions && (this.expandFilterResultControl.value || atLeastOneViolationCustomStatus)),
      tooltip: !(userHasPermissions && (this.expandFilterResultControl.value || atLeastOneViolationCustomStatus))
        ? waiveTooltip
        : tooltip,
    } as MenuAction);

    this.multiSelectedMenuActions$.next(menuActions);
  }

  private _onAllRowsSelectionClick(newValue: boolean | null) {
    const newBooleanValue = newValue ? true : false;
    const currentHygieneData = this.hygieneCurrentPage$.getValue();
    const updatedHygieneData = currentHygieneData.map((violationRow) => {
      violationRow.checkboxStatus = newBooleanValue;
      newBooleanValue
        ? this.checkedViolationIds.add(violationRow.id)
        : this.checkedViolationIds.delete(violationRow.id);
      return violationRow;
    });

    this._storeService.setHygienePageData(updatedHygieneData);

    this._updateSupportedActionsForSelectedRows();
  }

  private _subscribeToHandleFiltersOnChange() {
    this._filtersOnChangeSubscription$ = this.pageFilters.onUpdate$.subscribe((selectedFilters) => {
      this.optionsAreSelected = this.pageFilters.getAllSelectedOptions().length > 0;
      this._fetchFirstPage(selectedFilters, this.pageFilters.sortedColumn$.value);
    });
  }

  private _subscribeToSortOnChange() {
    this._sortOnChangeSubscription$ = this.pageFilters.sortedColumn$.subscribe((newSort) => {
      this._fetchFirstPage(this.pageFilters.getSelectedFilters(), newSort);
    });
  }

  private _fetchByPage(continuationToken: string) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    this._fetchByPageSubscription$ = this._violationsHttpService
      .getHygieneByContinuationToken(continuationToken)
      .subscribe({
        next: (response) => this._handleViolationCollectionResponse(response),
        error: (error: HttpErrorResponse) => this._fetchRequestOnError(error),
      });
  }

  private _fetchFirstPage(filterOptions: ISelectedFilters, sort?: SortColumn) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    if (sort) {
      this.sorts = [sort];
    }

    this._firstPageSubscription$ = this._violationsHttpService.getHygiene(filterOptions, this.sorts).subscribe({
      next: (response) => this._handleViolationCollectionResponse(response),
      error: (error: HttpErrorResponse) => this._fetchRequestOnError(error),
    });
  }

  private _cancelOngoingHttpRequest() {
    if (this._firstPageSubscription$) {
      this._firstPageSubscription$.unsubscribe();
    }

    if (this._fetchByPageSubscription$) {
      this._fetchByPageSubscription$.unsubscribe();
    }
  }

  private _fetchRequestOnError(error: HttpErrorResponse) {
    this._storeService.setHygienePageData([]);
    this.isLoading$.next(false);
    this.showNoData = true;
    this._httpErrorHandlerService.handle(error);
  }

  private _tableBehaviorChangeBeforeFetch() {
    this._storeService.setHygienePageData([]);
    this.isLoading$.next(true);
    this.checkedViolationIds.clear();
  }

  private async _handleViolationCollectionResponse(response: JsonHalCollection<ViolationModel>) {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);

    const violations = response._embedded.items;

    if (!violations || violations.length == 0) {
      this.showNoData = true;
    } else {
      this.showNoData = false;
      this._currentViolationsById = {};
      const violationsViewModelTemp = violations.map((violation) => {
        this._currentViolationsById[violation.id] = violation;
        return this._transformViolationResponse(violation);
      });
      await this._violationsActionsService.setActionsForViolations(violations, violationsViewModelTemp);
      this._storeService.setHygienePageData(violationsViewModelTemp);
    }
    this.isLoading$.next(false);
  }

  private _handlePaginationUrls(links: JsonHalLinks) {
    if (!this._paginationNavigation) {
      this._paginationNavigation = {
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel;
    } else {
      this._paginationNavigation.currentPage = links.self.href;
      this._paginationNavigation.nextPage = links.next?.href;
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
    this.totalItems = pageInfo.totalItems;
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize);

    if (!this.pageInfo$) {
      this.pageInfo$ = new BehaviorSubject(tablePageInfo);
    } else {
      this.pageInfo$.next(tablePageInfo);
    }
  }

  private _transformViolationResponse(violation: ViolationModel): IViolationsViewModel {
    const platform = violation.resource.platformInfo.platform;
    const errorMessage = populateErrorMessage(violation.resource);
    const createdByDisplayName = this._displayNameService.getCreatedByDisplayName(
      violation.resource?.identities?.createdBy,
      violation.resource.resourceAdditionalProperties
    );
    const ownerDisplayNames = violation.resource.identities?.ownedBy?.value
      ? violation.resource.identities.ownedBy.value
          .map((owner) => owner.displayName)
          .filter((displayName) => displayName)
      : [];
    return {
      rowId: violation.id,
      id: violation.id,
      name: violation.rule.displayName,
      labels: violation.labelBindings,
      toolTipViolationName: errorMessage != '' ? errorMessage : null,
      iconSrcViolationName: 'assets/images/status-icons/error.svg',
      resourceName: violation.resource.displayName,
      resourceType: this._displayCamelCaseStringPipe.transform(violation.resource.resourceType),
      createdBy: createdByDisplayName,
      notApplicableCreatedByTooltipText:
        this._textProviderService.getNotApplicableTableTooltipTextByResourceTypeIfNeeded(
          violation.resource.resourceType,
          FieldName.createdBy,
          createdByDisplayName
        ),
      resourceTypeIcon: getResourceCategoryIconPath(violation.resource.resourceCategory),
      ownedBy: ownerDisplayNames,
      notApplicableOwnedByTooltipText: this._textProviderService.getNotApplicableTableTooltipTextByResourceTypeIfNeeded(
        violation.resource.resourceType,
        FieldName.ownedBy,
        ownerDisplayNames[0]
      ),
      platform: platform,
      platformIcon: getPlatformLogoPath(platform),
      resourceStatusLabel: InventoryHelper.getViolationRelatedResourceStatusLabel(
        violation.resource,
        violation.statusReason
      ),
      owasp: owaspLcncToDisplayName[violation.rule.owasp[0]],
      severity: violation.rule.severity.toString(),
      firstSeen: formatFromDate(violation.firstSeen),
      statusLabel: ViolationRowHelpers.getStatusLabel(violation.status),
      menuActions: [],
      clickable: true,
      resourceId: violation.resource.platformInfo.resourceId,
      ruleId: violation.rule.id,
      integrationId: violation.integration.id,
      ruleTags: violation.rule.tags,
      violation: violation,
    } as IViolationsViewModel;
  }

  private _fetchPageWhenTokenExist(url: string) {
    const continuationToken = this._tokenExtractor(url);

    if (continuationToken) {
      this._fetchByPage(continuationToken);
    }
  }

  private _tokenExtractor(url: string): string | null {
    const continuationToken = new URLSearchParams(url.slice(url.indexOf('?'))).get('continuationToken');
    if (!continuationToken) {
      this.logger.error('Failed to extract continuation token from json hal next inventory resource page link');
      return null;
    }

    return continuationToken;
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 0,
      maxSize: this.defaultPageSize,
      totalItems: 0,
      totalPages: 1,
    });
  }

  private _handleViolationStatusUpdateActionResponse(
    actionResponse: Observable<ViolationsStatusUpdateResponse | undefined>,
    rowActionClicked: RowActionClicked
  ) {
    const violationRow = rowActionClicked.row.getValue() as IViolationsViewModel;
    this._currentViolationsById[violationRow.id];

    actionResponse.subscribe(
      (response: ViolationsStatusUpdateResponse | undefined) => {
        if (response) {
          const notificationText = 'Violation was updated, refreshing page';
          this._notificationService.success(notificationText);
          this._refreshCurrentPage();
        }
      },
      (error) => this._notificationService.error(error.error.detail)
    );
  }

  private _handleResourceDeleteActionResponse(actionResponse: Observable<boolean>) {
    actionResponse.subscribe((resp) => {
      if (resp) {
        this._refreshCurrentPage();
      }
    });
  }

  private _refreshCurrentPage() {
    const continuationToken = this._tokenExtractor(this._paginationNavigation.currentPage);
    if (continuationToken) {
      this._fetchByPage(continuationToken);
    }
  }

  private _resetCheckedViolationsStatus(
    violationsByIntegration: { [integrationId: string]: ViolationModel[] },
    justificationContent: string
  ) {
    const requests = Object.keys(violationsByIntegration).map((integrationId) => {
      const resetModel = {
        ids: violationsByIntegration[integrationId].map((violation) => violation.id),
        reset_reason: {
          justification: justificationContent,
        } as ViolationChangeJustificationApiModel,
      } as ResetViolationsStatusApiModel;

      return this._violationsHttpService.resetViolationStatus(integrationId, resetModel);
    });

    this.multiActionsIsLoading$.next(true);
    forkJoin(requests).subscribe({
      next: () => {
        this._handleMultiResponse(true);
      },
      error: () => {
        this._handleMultiResponse(false);
      },
    });
  }

  private _updateCheckedViolationsStatus(
    newStatus: UpdatableViolationStatus,
    violationsByIntegration: { [integrationId: string]: ViolationModel[] },
    justificationContent: string
  ) {
    const requests = Object.keys(violationsByIntegration).map((integrationId) => {
      const updateModel = {
        ids: violationsByIntegration[integrationId].map((violation) => violation.id),
        status: newStatus,
        change_reason: {
          justification: justificationContent,
        } as ViolationChangeJustificationApiModel,
      } as UpdateViolationsStatusApiModel;

      return this._violationsHttpService.updateViolationStatus(integrationId, updateModel);
    });

    this.multiActionsIsLoading$.next(true);
    forkJoin(requests).subscribe({
      next: () => {
        this._handleMultiResponse(true);
      },
      error: () => {
        this._handleMultiResponse(false);
      },
    });
  }

  private _handleMultiResponse(succeedResponse: boolean) {
    this.multiActionsIsLoading$.next(false);
    if (succeedResponse) {
      this._notificationService.success('Status updated, refreshing page');
    } else {
      this._notificationService.warning('Partially updated, refreshing page');
    }
    this.checkedViolationIds = new Set();
    this.allRowsSelected.setValue(false);
    this._refreshCurrentPage();
  }
}
